import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePermission } from '../../../auth/AuthContext';
import { projectBeneficiariesWorkspaceApi } from '../../../api/projectWorkspace';
import { ApiError } from '../../../api/client';
import { useToast } from '../../../components/Toast';
import { Project, ProjectBeneficiaries } from '../../../types/projects';
import { LocationSelector } from './LocationSelector';
import { CommentThread } from '../../../components/CommentThread';
import { isProjectDesignEditable } from './projectEditLock';

export function ProjectLocationsTab({ project }: { project: Project }) {
  const canManageLocations = usePermission('project_locations.manage') && isProjectDesignEditable(project.status);
  const canManageBeneficiaries = usePermission('project_beneficiaries.manage') && isProjectDesignEditable(project.status);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  // Geographic breakdown of beneficiaries lives here rather than on the
  // Beneficiaries tab - it's about where people are, which belongs with
  // the rest of location data. Backed by the same project_beneficiaries
  // record; only this one field is edited from this tab.
  const beneficiariesQuery = useQuery({ queryKey: ['project-beneficiaries', project.id], queryFn: () => projectBeneficiariesWorkspaceApi.get(project.id) });
  const { register, handleSubmit } = useForm<Partial<ProjectBeneficiaries>>({ values: beneficiariesQuery.data?.data });

  const saveMutation = useMutation({
    mutationFn: (values: Partial<ProjectBeneficiaries>) => projectBeneficiariesWorkspaceApi.update(project.id, { geographic_breakdown_notes: values.geographic_breakdown_notes }),
    onSuccess: (result) => { showSuccess('Geographic breakdown saved.'); queryClient.setQueryData(['project-beneficiaries', project.id], { data: result.data }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save.'),
  });

  return (
    <>
      <section className="card locations-card">
        <h2>Implementation Locations</h2>
        <p className="muted">
          Search and select every state and LGA where this project will be implemented. Multiple locations can be selected.
        </p>
        <LocationSelector projectId={project.id} canManage={canManageLocations} />
        <CommentThread projectId={project.id} entityType="locations" />
      </section>

      <form onSubmit={handleSubmit((v) => saveMutation.mutate(v))}>
        <section className="card">
          <h2>Geographic Breakdown of Beneficiaries</h2>
          <p className="muted">How beneficiary numbers are distributed across the locations selected above.</p>
          <textarea rows={4} {...register('geographic_breakdown_notes')} disabled={!canManageBeneficiaries} />
          {canManageBeneficiaries && <div className="form-actions"><button type="submit" className="btn-primary" disabled={saveMutation.isPending}>{saveMutation.isPending ? 'Saving…' : 'Save'}</button></div>}
        </section>
      </form>
    </>
  );
}
